import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Plus, X, UserMinus } from 'lucide-react'

import { apiClient } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'

interface Permission {
  id: string
  user_id: string
  cost_center_id: string
  valid_from: string
  valid_to: string | null
  is_active: boolean
  granted_by: string
  created_at: string
  revoked_at: string | null
  user_display_name: string | null
  user_email: string | null
}

interface CostCenterWithPermissions {
  id: string
  code: string
  name: string
  description: string | null
  is_deleted: boolean
  organization_id: string
  permissions: Permission[]
}

interface User {
  id: string
  email: string
  display_name: string
  role: string
  is_active: boolean
}

interface UsersResponse {
  data: User[]
}

export default function CostCenterPermissionsPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation('admin')
  const { t: tc } = useTranslation('common')
  const queryClient = useQueryClient()

  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState('')
  const [validFrom, setValidFrom] = useState(() => new Date().toISOString().split('T')[0])
  const [validTo, setValidTo] = useState('')

  const { data: costCenter, isLoading, error } = useQuery<CostCenterWithPermissions>({
    queryKey: ['cost-center', id],
    queryFn: () => apiClient.get(`/cost-centers/${id}`),
    enabled: !!id,
  })

  const { data: usersData } = useQuery<UsersResponse>({
    queryKey: ['users', costCenter?.organization_id],
    queryFn: () => {
      const params = costCenter?.organization_id 
        ? `?organization_id=${costCenter.organization_id}` 
        : ''
      return apiClient.get(`/users${params}`)
    },
    enabled: showAddModal && !!costCenter,
  })

  const grantMutation = useMutation({
    mutationFn: (data: { user_id: string; valid_from: string; valid_to?: string }) =>
      apiClient.post(`/cost-centers/${id}/permissions`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-center', id] })
      setShowAddModal(false)
      setSelectedUserId('')
      setValidTo('')
    },
    onError: (error) => {
      console.error('Grant permission error:', error)
      alert(`Virhe: ${error instanceof Error ? error.message : 'Tuntematon virhe'}`)
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (permissionId: string) =>
      apiClient.delete(`/cost-centers/${id}/permissions/${permissionId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-center', id] })
    },
  })

  const handleGrant = (e: React.FormEvent) => {
    e.preventDefault()
    grantMutation.mutate({
      user_id: selectedUserId,
      valid_from: validFrom,
      valid_to: validTo || undefined,
    })
  }

  const handleRevoke = (permission: Permission) => {
    if (window.confirm(t('costCenters.permissions.confirmRevoke', { name: permission.user_display_name || permission.user_email }))) {
      revokeMutation.mutate(permission.id)
    }
  }

  // Filter out users who already have active permission
  const availableUsers = usersData?.data.filter(
    (user) => !costCenter?.permissions.some((p) => p.user_id === user.id && p.is_active)
  ) || []

  if (isLoading) {
    return <LoadingSpinner />
  }

  if (error || !costCenter) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{tc('errors.generic')}</p>
        <Link to="/cost-centers" className="mt-4 inline-flex items-center text-primary-600 hover:text-primary-700">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {tc('common.back')}
        </Link>
      </div>
    )
  }

  const activePermissions = costCenter.permissions.filter((p) => p.is_active)
  const revokedPermissions = costCenter.permissions.filter((p) => !p.is_active)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link
            to="/cost-centers"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-2"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {tc('common.back')}
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('costCenters.permissions.title')}
          </h1>
          <p className="text-gray-600">
            {costCenter.code} - {costCenter.name}
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          {t('costCenters.permissions.add')}
        </button>
      </div>

      {/* Active Permissions */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">{t('costCenters.permissions.active')}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.permissions.user')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.permissions.validFrom')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.permissions.validTo')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.permissions.grantedAt')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('costCenters.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {activePermissions.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    {t('costCenters.permissions.noActive')}
                  </td>
                </tr>
              ) : (
                activePermissions.map((perm) => (
                  <tr key={perm.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">{perm.user_display_name || '-'}</div>
                      <div className="text-sm text-gray-500">{perm.user_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {new Date(perm.valid_from).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {perm.valid_to ? new Date(perm.valid_to).toLocaleDateString() : t('costCenters.permissions.indefinite')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(perm.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => handleRevoke(perm)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                        title={t('costCenters.permissions.revoke')}
                      >
                        <UserMinus className="w-5 h-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Revoked Permissions (History) */}
      {revokedPermissions.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold text-gray-600">{t('costCenters.permissions.history')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('costCenters.permissions.user')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('costCenters.permissions.validFrom')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('costCenters.permissions.validTo')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('costCenters.permissions.revokedAt')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {revokedPermissions.map((perm) => (
                  <tr key={perm.id} className="opacity-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">{perm.user_display_name || '-'}</div>
                      <div className="text-sm text-gray-500">{perm.user_email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {new Date(perm.valid_from).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {perm.valid_to ? new Date(perm.valid_to).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {perm.revoked_at ? new Date(perm.revoked_at).toLocaleDateString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Permission Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('costCenters.permissions.addTitle')}</h2>
              <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleGrant} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.permissions.selectUser')} *
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">{t('costCenters.permissions.selectUserPlaceholder')}</option>
                  {availableUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.display_name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.permissions.validFrom')} *
                </label>
                <input
                  type="date"
                  value={validFrom}
                  onChange={(e) => setValidFrom(e.target.value)}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('costCenters.permissions.validTo')}
                </label>
                <input
                  type="date"
                  value={validTo}
                  onChange={(e) => setValidTo(e.target.value)}
                  min={validFrom}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="text-xs text-gray-500 mt-1">{t('costCenters.permissions.validToHelp')}</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {tc('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={grantMutation.isPending || !selectedUserId}
                  className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                >
                  {grantMutation.isPending ? tc('common.saving') : t('costCenters.permissions.grant')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
